import {
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

interface UploadModalProps {
  isOpen: boolean;
  filesCount: number;
  completedCount: number;
  failedCount: number;
}

/**
 * Render the status icon based on upload state
 */
const renderStatusIcon = (allComplete: boolean, hasErrors: boolean) => {
  if (allComplete) {
    return hasErrors 
      ? <ExclamationTriangleIcon className="w-16 h-16 text-yellow-500" />
      : <CheckCircleIcon className="w-16 h-16 text-green-500" />;
  }
  
  return (
    <div className="relative w-20 h-20 flex items-center justify-center">
      <div className="absolute inset-0 border-4 border-gray-200 rounded-full" />
      <div className="absolute inset-0 border-4 border-[#0097a9] border-t-transparent rounded-full animate-spin" />
      <CloudArrowUpIcon className="w-12 h-12 text-[#0097a9] relative z-10" />
    </div>
  );
};

/**
 * Get the title text based on upload state
 */
const getTitleText = (allComplete: boolean, hasErrors: boolean): string => {
  if (allComplete) {
    return hasErrors ? 'Upload Complete with Errors' : 'Upload Complete';
  }
  return 'Uploading Files...';
};

/**
 * UploadModal - Blocking modal displayed during file upload operations
 * Prevents user interaction while uploads are in progress
 */
export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  filesCount,
  completedCount,
  failedCount,
}) => {
  if (!isOpen) {
    return null;
  }

  const uploadingCount = filesCount - completedCount - failedCount;
  const allComplete = completedCount + failedCount >= filesCount;
  const hasErrors = failedCount > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        aria-hidden="true"
      />

      {/* Modal centered */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {renderStatusIcon(allComplete, hasErrors)}
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
            {getTitleText(allComplete, hasErrors)}
          </h2>

          {/* Status message */}
          <div className="text-center text-gray-600 mb-6">
            {allComplete ? (
              <div className="space-y-1">
                {completedCount > 0 && (
                  <p className="text-green-600 font-medium">
                    {completedCount} of {filesCount} file
                    {filesCount !== 1 ? 's' : ''} uploaded successfully
                  </p>
                )}
                {failedCount > 0 && (
                  <p className="text-red-600 font-medium">
                    {failedCount} file{failedCount !== 1 ? 's' : ''} failed to
                    upload
                  </p>
                )}
              </div>
            ) : (
              <p className="font-medium">
                Processing {filesCount} file{filesCount !== 1 ? 's' : ''}...
              </p>
            )}
          </div>

          {/* Progress bar */}
          {!allComplete && (
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-[#0097a9] h-2.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${((completedCount + failedCount) / filesCount) * 100}%`,
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>
                  {completedCount + failedCount} of {filesCount}
                </span>
                <span>
                  {Math.round(
                    ((completedCount + failedCount) / filesCount) * 100,
                  )}
                  %
                </span>
              </div>
            </div>
          )}

          {/* Status breakdown */}
          <div className="flex justify-center gap-6 text-sm">
            {uploadingCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-gray-600">
                  {uploadingCount} uploading
                </span>
              </div>
            )}
            {completedCount > 0 && (
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500" />
                <span className="text-gray-600">
                  {completedCount} completed
                </span>
              </div>
            )}
            {failedCount > 0 && (
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                <span className="text-gray-600">{failedCount} failed</span>
              </div>
            )}
          </div>

          {/* Helper text */}
          {!allComplete && (
            <p className="text-xs text-center text-gray-500 mt-4">
              Please do not close this window or navigate away
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
